// The bare singleton, not the app's bootstrap: these helpers must not drag
// i18next initialisation into every module and test that imports a tour.
import i18next from 'i18next';
import type { OrderHistory } from '../../stores/appOrderHistoryStore';
import type { RevenueHistory } from '../../services/revenuehistory/revenueHistoryService';
import { useOrderHistory } from '../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../stores/appRevenueHistoryStore';
import { useRestaurantStore } from '../../stores/appRestaurantStore';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../stores/appUserDefaultStore';
import { useFiltersStore, type OverallTypeFilter } from '../../stores/appFiltersStore';
import { useTimeIntervalStore } from '../../stores/appTimeIntervalStore';
import { filterOrdersByDateRange, filterRevenueByDateRange } from '../../utils/dateRangeFilters';
import { getPreviousPeriodRange } from '../../utils/kpiChangeCalculations';
import { filterAllOrders, filterAllRevenue } from '../../utils/filterUtils';
import { useCostPriceStore } from '../../stores/appCostPriceStore';
import { useCostPriceCategoriesStore } from '../../stores/appCostPriceCategoriesStore';
import { filterOrdersByOverallType } from '../orderhistory/utils';
import { filterRevenueByOverallType } from '../revenuehistory/utils';
import { currencyFormatter } from '../../services/helperService';
import type { View } from '../orderhistory/types';

/**
 * Live figures for the tour cards.
 *
 * Every card that names a number reads it here at render time, so the sentence and
 * the card underneath it can never disagree. Each getter also returns a `context`,
 * which i18next appends to the description key (`…description_up`): that is how a
 * sentence claiming a direction — up, down, level, which category leads — only ever
 * ships when the data actually shows it. An empty `context` falls back to the base
 * key, which everywhere here is the neutral wording for an account with no data yet.
 */

/** One decimal, matching the KPI cards; 0.04% and 0.0% must not read differently. */
export const formatPct = (n: number) =>
  `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;

/** Compact euros for a sentence: €12.4k reads better mid-paragraph than €12,431.09. */
export function formatEuroShort(n: number) {
  if (Math.abs(n) >= 1000) {
    const k = n / 1000;
    const digits = Math.abs(k) >= 100 ? 0 : 1;
    return `€${k.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}k`;
  }
  return currencyFormatter.format(n);
}

export const formatEuro = (n: number) => currencyFormatter.format(n);

/** Anything that rounds away at one decimal is "level", not a rise or a fall. */
const FLAT_BAND = 0.05;

export type Direction = 'up' | 'down' | 'flat';

export function directionOf(changePct: number): Direction {
  if (Math.abs(changePct) < FLAT_BAND) return 'flat';
  return changePct > 0 ? 'up' : 'down';
}

/** Translated names for the food / drinks / non-food sides, as the charts label them. */
export function sideLabel(side: 'food' | 'drinks' | 'non-food'): string {
  if (side === 'food') return i18next.t('overallType.food');
  if (side === 'drinks') return i18next.t('overallType.drinks');
  return i18next.t('overallType.nonFood');
}

/** `calculateSpendingSplit` labels its slices in English; map back to a side. */
export function sideFromSplitName(name: string): 'food' | 'drinks' | 'non-food' {
  if (name === 'Food') return 'food';
  if (name === 'Drinks') return 'drinks';
  return 'non-food';
}

/**
 * "vs last week" — the same label the KPI card prints under its arrow, built the same
 * way `getChangeLabel` builds it so the card and the sentence name the same period.
 */
export function comparisonLabel(view: View, startDate: Date): string {
  if (view === 'day') {
    const locale = i18next.language === 'nl' ? 'nl-NL' : 'en-GB';
    const day = new Date(startDate.getTime() - 7 * 24 * 60 * 60 * 1000)
      .toLocaleDateString(locale, { weekday: 'short' });
    return i18next.t('common.vsLastDay', { day });
  }
  if (view === 'week') return i18next.t('common.vsLastWeek');
  if (view === 'month') return i18next.t('common.vsLastMonth');
  return '';
}

/**
 * Which page's filter pipeline to replay. The dashboard applies only the restaurant
 * and overall-type filters; the order and revenue pages each add their own column
 * filters on top, and a tour replayed on a filtered page must total what those pages
 * are showing, not what an unfiltered one would.
 */
export type TourScope = 'dashboard' | 'orders' | 'revenue';

export interface TourSnapshot {
  view: View;
  startDate: Date;
  endDate: Date;
  overallTypeFilter: OverallTypeFilter;
  showExVatOrders: boolean;
  showExVatRevenue: boolean;
  /** Restaurant-scoped but not date-scoped — what the change helpers need. */
  baseOrders: OrderHistory[];
  baseRevenue: RevenueHistory[];
  /** Restaurant + date scoped, before the food/drinks filter (the split charts use this). */
  dateOrders: OrderHistory[];
  dateRevenue: RevenueHistory[];
  /** What the KPI cards actually total. */
  orders: OrderHistory[];
  revenue: RevenueHistory[];
}

/**
 * Mirror of the pipeline the page behind the step runs before it totals anything, so a
 * tour replayed on a filtered page quotes the figures actually on screen. Each scope
 * follows its own page: the dashboard scopes by restaurant by hand, the order pages
 * hand restaurant scoping to `filterAllOrders`, the revenue pages to `filterAllRevenue`.
 */
export function readTourSnapshot(scope: TourScope): TourSnapshot {
  const { startDate, endDate, view } = useTimeIntervalStore.getState();
  const filterState = useFiltersStore.getState();
  const { overallTypeFilter, restaurantFilters } = filterState;
  const defaults = useAppUserDefaultStore.getState();
  const allRestaurants = isAllRestaurantsSelected(defaults.restaurant);
  const restaurantScope = allRestaurants && restaurantFilters.length > 0 ? restaurantFilters : undefined;

  const restaurantStore = useRestaurantStore.getState();
  const revenueSourcesMap = restaurantStore.restaurantRevenueSources;
  const restaurantOfSource: Record<string, string> = {};
  for (const [restaurantId, sources] of Object.entries(revenueSourcesMap)) {
    for (const source of sources) restaurantOfSource[source.id] = restaurantId;
  }

  const allOrders = useOrderHistory.getState().orderHistoryList;
  const allRevenue = useRevenueHistory.getState().revenueHistoryList;

  /** The dashboard's own restaurant scoping, applied before anything else. */
  const byRestaurant = <T,>(rows: T[], restaurantOf: (row: T) => string | undefined) =>
    restaurantScope
      ? rows.filter(row => {
          const id = restaurantOf(row);
          return Boolean(id && restaurantScope.includes(id));
        })
      : rows;

  // Only the orders and revenue pages carry column filters; the dashboard has none.
  const orderFilters = {
    platforms: scope === 'orders' ? filterState.platformFilters : [],
    sections: scope === 'orders' ? filterState.sectionFilters : [],
    categories: scope === 'orders' ? filterState.categoryFilters : [],
    subCategories: [] as string[],
  };
  const revenueFilters = {
    revenueCategories: scope === 'revenue' ? filterState.revenueCategoryFilters : [],
    revenueSources: scope === 'revenue' ? filterState.revenueSourceFilters : [],
    menuCategories: scope === 'revenue' ? filterState.menuCategoryFilters : [],
  };
  const pageRevenueSources = allRestaurants
    ? Object.values(revenueSourcesMap).flat()
    : (defaults.restaurant ? revenueSourcesMap[defaults.restaurant.id] ?? [] : []);

  /**
   * Orders as the page behind this scope filters them. `withType` is off for the
   * food/drinks split, which needs both sides and does its own splitting.
   */
  const scopeOrders = (rows: OrderHistory[], withType = true): OrderHistory[] => {
    const typed = withType ? filterOrdersByOverallType(rows, overallTypeFilter) : rows;
    if (scope !== 'orders') {
      return byRestaurant(typed, order => order.restaurantSection?.restaurantId || order.restaurantId);
    }
    return filterAllOrders(typed, orderFilters, restaurantScope, restaurantStore.platformToSectionIndex);
  };

  /** Revenue likewise. Only the revenue pages apply `filterAllRevenue`. */
  const scopeRevenue = (rows: RevenueHistory[], withType = true): RevenueHistory[] => {
    const typed = withType ? filterRevenueByOverallType(rows, overallTypeFilter) : rows;
    if (scope !== 'revenue') {
      return byRestaurant(typed, entry => entry.restaurantId || restaurantOfSource[entry.restaurantRevenueSourceId]);
    }
    return filterAllRevenue(
      typed,
      revenueFilters,
      useCostPriceStore.getState().costPriceList,
      useCostPriceCategoriesStore.getState().categories,
      restaurantScope,
      pageRevenueSources
    );
  };

  const dateOrders = filterOrdersByDateRange(allOrders, startDate, endDate);
  const dateRevenue = filterRevenueByDateRange(allRevenue, startDate, endDate);

  return {
    view,
    startDate,
    endDate,
    overallTypeFilter,
    showExVatOrders: defaults.showExVatOrders,
    showExVatRevenue: defaults.showExVat,
    baseOrders: scopeOrders(allOrders),
    baseRevenue: scopeRevenue(allRevenue),
    dateOrders: scopeOrders(dateOrders, false),
    dateRevenue: scopeRevenue(dateRevenue, false),
    orders: scopeOrders(dateOrders),
    revenue: scopeRevenue(dateRevenue),
  };
}

/**
 * The previous period's total, or null when there is nothing to compare against —
 * the all-time view, or a previous period with no data. `calculateKpiChange` reports
 * both of those as a 0% change, which a card can render as a dash but a sentence
 * cannot: "level with last week" would be a claim about a week we know nothing about.
 */
export function previousPeriodTotal<T>(
  snapshot: TourSnapshot,
  list: T[],
  total: (rows: T[], start: Date, end: Date) => number
): number | null {
  const range = getPreviousPeriodRange(snapshot.view, snapshot.startDate, snapshot.endDate);
  if (!range) return null;
  const value = total(list, range.previousStartDate, range.previousEndDate);
  return value === 0 ? null : value;
}

export function percentageChange(current: number, previous: number): number {
  return ((current - previous) / previous) * 100;
}
